import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../hooks/useAlert';
import { authManager } from '../../utils/authManager';
import { getGlobalAlertEventName, GlobalAlertEventData } from '../../utils/globalAlert';
import { AlertMessage } from './AlertMessage';
import { ALERT_Z_INDEX } from '../../utils/alertManager';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider 组件
 * 
 * 主要作用：
 * 1. 设置认证管理器的回调函数（用于 token 过期时统一处理）
 *     - alertCallback: 用于显示错误提示
 *     - navigateCallback: 用于跳转到登录页
 * 2. 监听全局 Alert 事件，用于在非 React 组件环境中显示 Alert
 * 3. 渲染全局 AlertMessage 组件，用于显示所有全局 Alert 提示
 * 
 * 注意：用户数据初始化已移至 NavBar 组件，在页面跳转后执行
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { alertConfig, showAlert, hideAlert } = useAlert();

  useEffect(() => {
    // 设置认证管理器的回调函数
    // 这些回调函数用于 API 请求拦截器中检测到 token 过期时调用
    authManager.setAlertCallback(showAlert);
    authManager.setNavigateCallback(navigate);
  }, [navigate, showAlert]);

  useEffect(() => {
    // 监听全局 Alert 事件
    const handleGlobalAlert = (event: Event) => {
      const customEvent = event as CustomEvent<GlobalAlertEventData>;
      const { type, message, closable } = customEvent.detail;
      if (message) {
        showAlert(type, message, closable);
      }
    };

    const eventName = getGlobalAlertEventName();
    window.addEventListener(eventName, handleGlobalAlert);

    return () => {
      window.removeEventListener(eventName, handleGlobalAlert);
    };
  }, [showAlert]);

  return (
    <>
      {/* 全局 AlertMessage 组件，用于显示所有全局 Alert 提示 */}
      <AlertMessage 
        config={alertConfig}
        onClose={hideAlert}
        zIndex={ALERT_Z_INDEX.NAVBAR}
      />
      {children}
    </>
  );
};
